import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

import '../components/OrderList.css';

const OrderList = () => {
    const [orders, setOrders] = useState([]);
    const [showEntries, setShowEntries] = useState(10);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();

    // Fetch orders
    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await axios.get('https://newerp.awasthivitality.com/api/orders/list');
            
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                setOrders(response.data.data);
            } else {
                setOrders([]);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const handleViewOrder = (order) => {
        navigate(`/OrderDetails/${order.id}`, { state: { order } });
    };

    const handleShowEntriesChange = (e) => {
        setShowEntries(Number(e.target.value));
        setCurrentPage(1);
    };

    // Pagination logic
    const totalPages = Math.ceil(orders.length / showEntries);
    const startIndex = (currentPage - 1) * showEntries;
    const paginatedOrders = orders.slice(startIndex, startIndex + showEntries);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    return (
        <div className="main-container ml-desktop">
            <div className="header-section">
                <h1 className="main-title">Order - List</h1>
            </div>

            <div className="controls-row">
                <div className="entries-control">
                    <span>Show</span>
                    <select value={showEntries} onChange={handleShowEntriesChange} className="entries-select">
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <span>entries</span>
                </div>
            </div>

            {loading ? (
                <div className="text-center mt-4">Loading orders...</div>
            ) : (
                <>
                    {/* Desktop Table */}
                    <div className="table-container desktop-only">
                        <table className="orders-table">
                            <thead>
                                <tr>
                                    <th className="text-center">S.No.</th>
                                    <th>Date</th>
                                    <th>Order No</th>
                                    <th>Customer</th>
                                    <th className="text-center">Payment Mode</th>
                                    <th className="text-center">Total Amt</th>
                                    <th className="text-center">Discount</th>
                                    <th className="text-center">Grand Total</th>
                                    <th className="text-center">Received Amt</th>
                                    <th className="text-center">Pending Amt</th>
                                    <th>Pay Status</th>
                                    <th>Order Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedOrders.length > 0 ? (
                                    paginatedOrders.map((order) => (
                                        <tr key={order.id} className="table-row">
                                            <td className="table-cell text-center">{order.sno || startIndex + paginatedOrders.indexOf(order) + 1}</td>
                                            <td className="table-cell">{order.order_date}</td>
                                            <td className="table-cell font-semibold">{order.order_no}</td>
                                            <td className="table-cell">
                                                {order.customer_name} <br />
                                                <Link 
                                                    to={`/patient/details/${order.patient_id}`}
                                                    style={{ color: '#007bff', textDecoration: 'none' }}
                                                >
                                                    {order.pr_no}
                                                </Link>
                                            </td>
                                            <td className="table-cell text-center">{order.order_paymode}</td>
                                            <td className="table-cell text-center">{order.product_amount}</td>
                                            <td className="table-cell text-center">{order.discount}</td>
                                            <td className="table-cell text-center">{order.grand_total}</td>
                                            <td className="table-cell text-center">{order.received_amt}</td>
                                            <td className="table-cell text-center">{order.pending_amt}</td>
                                            <td className="table-cell">{order.order_paystatus}</td>
                                            <td className="table-cell">{order.status}</td>
                                            <td className="table-cell action-cell text-center">
                                                <Eye
                                                    size={18}
                                                    className="action-icon"
                                                    onClick={() => handleViewOrder(order)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="View Order Details"
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="13" className="table-cell text-center-none">
                                            No data available in table
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={handlePrevPage}
                                    className="page-btn"
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <span className="page-info">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    onClick={handleNextPage}
                                    className="page-btn"
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Mobile Cards */}
                    <div className="card-container mobile-only">
                        {paginatedOrders.length > 0 ? (
                            paginatedOrders.map((order) => (
                                <div key={order.id} className="order-card">
                                    <div className="card-header">
                                        <h3 className="card-title">Order #{order.order_no}</h3>
                                        <span className="card-subtitle">{order.order_date}</span>
                                    </div>
                                    <div className="card-body">
                                        <div className="card-item">
                                            <span className="card-label">Customer:</span>
                                            <span>
                                                {order.customer_name} ({order.pr_no})
                                            </span>
                                        </div>
                                        <div className="card-item">
                                            <span className="card-label">Payment Mode:</span>
                                            <span>{order.order_paymode}</span>
                                        </div>
                                        <div className="card-item">
                                            <span className="card-label">Total / Discount:</span>
                                            <span>{order.product_amount} / {order.discount}</span>
                                        </div>
                                        <div className="card-item">
                                            <span className="card-label">Grand Total:</span>
                                            <span className="font-semibold">{order.grand_total}</span>
                                        </div>
                                        <div className="card-item">
                                            <span className="card-label">Received / Pending:</span>
                                            <span>{order.received_amt} / {order.pending_amt}</span>
                                        </div>
                                        <div className="card-item">
                                            <span className="card-label">Pay Status:</span>
                                            <span className={`badge ${order.order_paystatus === 'Success' ? 'badge-success' : 'badge-failure'}`}>
                                                {order.order_paystatus}
                                            </span>
                                        </div>
                                        <div className="card-item">
                                            <span className="card-label">Order Status:</span>
                                            <span className="badge badge-pending">{order.status}</span>
                                        </div>
                                        <div className="card-action">
                                            <button className="btn-view" onClick={() => handleViewOrder(order)}>
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-results-message">
                                No orders found.
                            </div>
                        )}

                        {/* Pagination Controls for Mobile */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={handlePrevPage}
                                    className="page-btn"
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <span className="page-info">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    onClick={handleNextPage}
                                    className="page-btn"
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default OrderList;